import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  AlertTriangle, 
  ArrowRight, 
  Download,
  Calendar,
  Beaker,
  Heart,
  Brain,
  Shield,
  Scale,
  Flame,
  Pill,
  Activity,
  Users,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';
import { PCOSAssessmentResult, PCOSPhenotype, PCOSEndotype } from '@/lib/pcos/types';

// ============================================================================
// PHENOTYPE & ENDOTYPE DATA
// ============================================================================

const phenotypeData: Record<PCOSPhenotype, { 
  name: string; 
  color: string; 
  description: string;
  icon: React.ElementType;
}> = {
  'A': {
    name: 'Classic PCOS (Type A)',
    color: '#FF6B6B',
    description: 'Full spectrum PCOS with all three Rotterdam criteria present: elevated androgens, irregular ovulation, and polycystic ovaries.',
    icon: Activity,
  },
  'B': {
    name: 'Non-PCO PCOS (Type B)',
    color: '#2FFFC7',
    description: 'Elevated androgens and irregular ovulation, but ovaries appear normal on imaging. Often diagnosed based on symptoms and labs alone.',
    icon: Beaker,
  },
  'C': {
    name: 'Ovulatory PCOS (Type C)',
    color: '#45B7D1',
    description: 'Elevated androgens and polycystic ovaries, but cycles may appear regular. Hormones are still imbalanced despite regular bleeds.',
    icon: Heart,
  },
  'D': {
    name: 'Non-Hyperandrogenic PCOS (Type D)',
    color: '#96CEB4',
    description: 'Irregular ovulation and polycystic ovaries without significant androgen excess. Milder symptoms but still affects fertility and metabolic health.',
    icon: Brain,
  },
  'uncertain': {
    name: 'Assessment Inconclusive',
    color: '#9CA3AF',
    description: 'Your responses don\'t fully meet Rotterdam criteria. Some symptoms are present but further evaluation is recommended.',
    icon: Info,
  },
};

const endotypeData: Record<PCOSEndotype, {
  name: string;
  color: string;
  description: string;
  icon: React.ElementType;
}> = {
  'insulin-resistant': {
    name: 'Insulin-Resistant',
    color: '#FF9F43',
    description: 'Your PCOS appears primarily driven by insulin resistance. Focus on blood sugar management through diet, exercise, and potentially medications.',
    icon: Scale,
  },
  'inflammatory': {
    name: 'Inflammatory',
    color: '#26DE81',
    description: 'Chronic inflammation appears to be a key driver. Address gut health, identify food sensitivities, and reduce inflammatory triggers.',
    icon: Shield,
  },
  'adrenal': {
    name: 'Adrenal',
    color: '#FC5C65',
    description: 'Signs of adrenal involvement with elevated DHEA-S. Prioritize stress management, quality sleep, and adrenal support strategies.',
    icon: Flame,
  },
  'post-pill': {
    name: 'Post-Pill',
    color: '#A55EEA',
    description: 'Symptoms appear related to recent discontinuation of hormonal contraceptives. Often resolves over 6-12 months with supportive care.',
    icon: Pill,
  },
  'mixed': {
    name: 'Mixed Presentation',
    color: '#FF6B9D',
    description: 'Multiple underlying factors contribute to your PCOS. A comprehensive approach addressing several causes will be most effective.',
    icon: Users,
  },
  'uncertain': {
    name: 'Uncertain',
    color: '#9CA3AF',
    description: 'The underlying driver isn\'t clearly defined. Work with a healthcare provider to identify personalized treatment approaches.',
    icon: Info,
  },
};

// ============================================================================
// COMPONENTS
// ============================================================================

interface ScoreBarProps {
  label: string;
  percentage: number;
  color?: string;
}

const ScoreBar: React.FC<ScoreBarProps> = ({ label, percentage, color = '#2FFFC7' }) => (
  <div className="mb-4">
    <div className="flex justify-between mb-1">
      <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{label}</span>
      <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{Math.round(percentage)}%</span>
    </div>
    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ backgroundColor: color, width: `${percentage}%` }}
      />
    </div>
  </div>
);

interface CollapsibleSectionProps {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div 
      className="rounded-xl overflow-hidden"
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between transition-colors"
        style={{ background: isOpen ? 'rgba(255, 255, 255, 0.02)' : 'transparent' }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(47, 255, 199, 0.1)' }}
          >
            <Icon className="w-4 h-4" style={{ color: '#2FFFC7' }} />
          </div>
          <span className="text-sm font-medium" style={{ color: '#E6E9F0' }}>{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.4)' }} />
        ) : (
          <ChevronDown className="w-4 h-4" style={{ color: 'rgba(255, 255, 255, 0.4)' }} />
        )}
      </button>
      {isOpen && (
        <div className="p-4 pt-0" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MAIN RESULTS PAGE
// ============================================================================

const PCOSResults: React.FC = () => {
  const navigate = useNavigate();
  const [result, setResult] = useState<PCOSAssessmentResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedResult = sessionStorage.getItem('pcosResult');
    
    if (storedResult) {
      try {
        const parsed = JSON.parse(storedResult);
        setResult(parsed.result || parsed);
      } catch (e) {
        console.error('Failed to parse result:', e);
      }
    }
    
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0a0f1a' }}>
        <div className="text-center">
          <div 
            className="w-12 h-12 rounded-full animate-spin mx-auto mb-4"
            style={{ border: '2px solid #2FFFC7', borderTopColor: 'transparent' }}
          />
          <p style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Loading your results...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#0a0f1a' }}>
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4" style={{ color: '#f97316' }} />
          <h2 className="text-2xl font-light mb-2" style={{ color: '#E6E9F0' }}>No Results Found</h2>
          <p className="mb-6" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            It looks like you haven't completed the PCOS assessment yet, or your session has expired.
          </p>
          <button
            onClick={() => navigate('/pcos/quiz')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105"
            style={{ background: '#2FFFC7', color: '#000' }}
          >
            Take the Assessment
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const phenotype = phenotypeData[result.likelyPhenotype];
  const endotype = endotypeData[result.likelyEndotype];
  const PhenotypeIcon = phenotype.icon;
  const EndotypeIcon = endotype.icon;

  return (
    <div className="min-h-screen py-12 px-6" style={{ background: 'linear-gradient(180deg, #0a0f1a 0%, #131a2b 100%)' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ background: 'rgba(47, 255, 199, 0.1)', border: '1px solid rgba(47, 255, 199, 0.3)' }}
          >
            <CheckCircle className="w-4 h-4" style={{ color: '#2FFFC7' }} />
            <span className="text-sm" style={{ color: '#2FFFC7' }}>Assessment Complete</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-light mb-4" style={{ color: '#E6E9F0' }}>
            Your PCOS <span className="italic" style={{ color: '#2FFFC7' }}>Profile</span>
          </h1>
          
          <p className="max-w-2xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Based on your responses, here's what we found about your hormonal health. 
            Remember, this is an educational assessment—not a diagnosis.
          </p>
        </div>

        {/* Primary Results */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Phenotype Card */}
          <div 
            className="p-6 rounded-2xl"
            style={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${phenotype.color}20` }}
              >
                <PhenotypeIcon className="w-6 h-6" style={{ color: phenotype.color }} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Likely Phenotype</p>
                <h2 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>{phenotype.name}</h2>
              </div>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Confidence</span>
                <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{Math.round(result.phenotypeConfidence)}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                <div 
                  className="h-full rounded-full"
                  style={{ backgroundColor: phenotype.color, width: `${result.phenotypeConfidence}%` }}
                />
              </div>
            </div>
            
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {phenotype.description}
            </p>
          </div>

          {/* Endotype Card */}
          <div 
            className="p-6 rounded-2xl"
            style={{
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${endotype.color}20` }}
              >
                <EndotypeIcon className="w-6 h-6" style={{ color: endotype.color }} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Likely Endotype</p>
                <h2 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>{endotype.name}</h2>
              </div>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Confidence</span>
                <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>{Math.round(result.endotypeConfidence)}%</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                <div 
                  className="h-full rounded-full"
                  style={{ backgroundColor: endotype.color, width: `${result.endotypeConfidence}%` }}
                />
              </div>
            </div>
            
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              {endotype.description}
            </p>
          </div>
        </div>

        {/* Risk Flags */}
        {Object.values(result.riskFlags).some(Boolean) && (
          <div 
            className="p-6 rounded-2xl mb-8"
            style={{ background: 'rgba(249, 115, 22, 0.1)', border: '1px solid rgba(249, 115, 22, 0.2)' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-5 h-5" style={{ color: '#f97316' }} />
              <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>Important Considerations</h3>
            </div>
            <ul className="space-y-2">
              {result.riskFlags.highInsulinResistance && (
                <li className="flex items-start gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: '#f97316' }} />
                  <span><strong style={{ color: '#E6E9F0' }}>High insulin resistance indicated.</strong> This significantly impacts PCOS and should be addressed with lifestyle changes and possibly medication.</span>
                </li>
              )}
              {result.riskFlags.metabolicSyndromeRisk && (
                <li className="flex items-start gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: '#f97316' }} />
                  <span><strong style={{ color: '#E6E9F0' }}>Metabolic syndrome risk.</strong> Consider comprehensive metabolic evaluation with your healthcare provider.</span>
                </li>
              )}
              {result.riskFlags.mentalHealthConcern && (
                <li className="flex items-start gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: '#f97316' }} />
                  <span><strong style={{ color: '#E6E9F0' }}>Mental health screening recommended.</strong> PCOS often co-occurs with anxiety and depression. Support is available.</span>
                </li>
              )}
              {result.riskFlags.fertilityConcern && (
                <li className="flex items-start gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: '#f97316' }} />
                  <span><strong style={{ color: '#E6E9F0' }}>Fertility considerations identified.</strong> If trying to conceive, early intervention can improve outcomes.</span>
                </li>
              )}
              {result.riskFlags.possibleTumor && (
                <li className="flex items-start gap-2 text-sm" style={{ color: '#ef4444' }}>
                  <div className="w-1.5 h-1.5 rounded-full mt-2" style={{ background: '#ef4444' }} />
                  <span><strong>Very high androgen levels reported.</strong> This requires urgent medical evaluation to rule out other causes.</span>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Detailed Scores */}
        <div className="space-y-4 mb-8">
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#E6E9F0' }}>Detailed Assessment</h3>
          
          <CollapsibleSection title="Rotterdam Criteria Scores" icon={Activity} defaultOpen>
            <div className="pt-4">
              <ScoreBar 
                label="Hyperandrogenism (Elevated Androgens)" 
                percentage={result.scores.hyperandrogenism.percentage}
                color="#FF6B6B"
              />
              <ScoreBar 
                label="Ovulatory Dysfunction" 
                percentage={result.scores.ovulatoryDysfunction.percentage}
                color="#2FFFC7"
              />
              <ScoreBar 
                label="Polycystic Ovaries" 
                percentage={result.scores.polycysticOvaries.percentage}
                color="#45B7D1"
              />
              <p className="text-xs mt-4" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                PCOS diagnosis requires 2 of 3 criteria. Scores above 50% suggest the criterion may be met.
              </p>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Endotype Factors" icon={Beaker}>
            <div className="pt-4">
              <ScoreBar 
                label="Insulin Resistance" 
                percentage={result.scores.insulinResistance.percentage}
                color="#FF9F43"
              />
              <ScoreBar 
                label="Inflammation" 
                percentage={result.scores.inflammation.percentage}
                color="#26DE81"
              />
              <ScoreBar 
                label="Adrenal Factors" 
                percentage={result.scores.adrenalFactors.percentage}
                color="#FC5C65"
              />
              <ScoreBar 
                label="Post-Pill Factors" 
                percentage={result.scores.postPillFactors.percentage}
                color="#A55EEA"
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Health Risk Assessment" icon={Shield}>
            <div className="pt-4">
              <ScoreBar 
                label="Metabolic Risk" 
                percentage={result.scores.metabolicRisk.percentage}
                color="#FF6B6B"
              />
              <ScoreBar 
                label="Mental Health Risk" 
                percentage={result.scores.mentalHealthRisk.percentage}
                color="#A55EEA"
              />
              <ScoreBar 
                label="GLP-1 Suitability" 
                percentage={result.scores.glp1Suitability.percentage}
                color="#2FFFC7"
              />
            </div>
          </CollapsibleSection>
        </div>

        {/* GLP-1 Assessment */}
        {result.glp1Assessment && (
          <div 
            className="p-6 rounded-2xl mb-8"
            style={{
              background: result.glp1Assessment.contraindicated 
                ? 'rgba(239, 68, 68, 0.1)' 
                : result.glp1Assessment.suitable
                  ? 'rgba(47, 255, 199, 0.1)'
                  : 'rgba(255, 255, 255, 0.02)',
              border: result.glp1Assessment.contraindicated 
                ? '1px solid rgba(239, 68, 68, 0.2)' 
                : result.glp1Assessment.suitable
                  ? '1px solid rgba(47, 255, 199, 0.2)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-5 h-5" style={{ 
                color: result.glp1Assessment.contraindicated 
                  ? '#ef4444' 
                  : result.glp1Assessment.suitable 
                    ? '#2FFFC7' 
                    : 'rgba(255, 255, 255, 0.5)'
              }} />
              <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>GLP-1 Assessment</h3>
            </div>
            
            {result.glp1Assessment.contraindicated ? (
              <div>
                <p className="text-sm mb-3" style={{ color: '#ef4444' }}>
                  <strong>GLP-1 medications are contraindicated</strong> based on your responses.
                </p>
                <ul className="space-y-1">
                  {result.glp1Assessment.reasons.map((reason, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      <AlertTriangle className="w-3 h-3" style={{ color: '#ef4444' }} />
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            ) : result.glp1Assessment.suitable ? (
              <div>
                <p className="text-sm mb-3" style={{ color: '#2FFFC7' }}>
                  <strong>You may be a candidate for GLP-1 therapy</strong> based on your metabolic profile.
                </p>
                <ul className="space-y-1 mb-4">
                  {result.glp1Assessment.reasons.map((reason, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      <CheckCircle className="w-3 h-3" style={{ color: '#2FFFC7' }} />
                      {reason}
                    </li>
                  ))}
                </ul>
                <p className="text-xs mt-4" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                  Note: Standard therapeutic doses are evidence-based. Microdosing is experimental and not FDA-approved.
                </p>
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Based on your responses, GLP-1 medications may not be the first-line treatment for your PCOS presentation. 
                Discuss all options with your healthcare provider.
              </p>
            )}
          </div>
        )}

        {/* Recommendations */}
        <div className="space-y-4 mb-8">
          <h3 className="text-lg font-semibold mb-4" style={{ color: '#E6E9F0' }}>Personalized Recommendations</h3>
          
          {result.recommendations.lifestyle.length > 0 && (
            <CollapsibleSection title="Lifestyle Changes" icon={Heart} defaultOpen>
              <ul className="pt-4 space-y-2">
                {result.recommendations.lifestyle.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
                    {rec}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {result.recommendations.testing.length > 0 && (
            <CollapsibleSection title="Recommended Testing" icon={Beaker}>
              <ul className="pt-4 space-y-2">
                {result.recommendations.testing.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
                    {rec}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {result.recommendations.treatments.length > 0 && (
            <CollapsibleSection title="Treatment Options to Discuss" icon={Pill}>
              <ul className="pt-4 space-y-2">
                {result.recommendations.treatments.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
                    {rec}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}

          {result.recommendations.specialists.length > 0 && (
            <CollapsibleSection title="Specialists to Consider" icon={Users}>
              <ul className="pt-4 space-y-2">
                {result.recommendations.specialists.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#2FFFC7' }} />
                    {rec}
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          )}
        </div>

        {/* NimCore Recommendation */}
        <div 
          className="p-6 rounded-2xl mb-8"
          style={{
            background: 'linear-gradient(135deg, rgba(47, 255, 199, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
            border: '1px solid rgba(47, 255, 199, 0.2)',
          }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Beaker className="w-5 h-5" style={{ color: '#2FFFC7' }} />
            <h3 className="text-lg font-semibold" style={{ color: '#E6E9F0' }}>Track Your Progress with NovaMed</h3>
          </div>
          <p className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {result.nimCoreRecommendation.rationale}
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {result.nimCoreRecommendation.markersToTrack.map((marker, i) => (
              <span 
                key={i} 
                className="px-3 py-1 rounded-full text-xs"
                style={{ background: 'rgba(47, 255, 199, 0.1)', color: '#2FFFC7' }}
              >
                {marker}
              </span>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => navigate('/pcos/quiz')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl transition-all"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#E6E9F0',
            }}
          >
            <ArrowRight className="w-4 h-4" />
            Retake Quiz
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl transition-all"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#E6E9F0',
            }}
          >
            <Download className="w-4 h-4" />
            Download Results
          </button>
        </div>

        {/* Disclaimer */}
        <div 
          className="mt-12 p-6 rounded-xl"
          style={{
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <h4 className="text-sm font-semibold mb-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Important Disclaimer</h4>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.4)' }}>
            This assessment is for <strong>educational purposes only</strong> and does not constitute medical advice, 
            diagnosis, or treatment. The results are based on self-reported symptoms and cannot replace professional 
            medical evaluation. PCOS diagnosis requires clinical assessment, lab work, and often imaging studies. 
            Please consult with a qualified healthcare professional (endocrinologist, gynecologist, or reproductive 
            endocrinologist) for proper diagnosis and personalized treatment recommendations.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PCOSResults;

